import { useState } from 'react'

import { type EditReleaseFormRequest, OfReleaseFormsService } from '@api'
import { useUnit } from 'effector-react'
import { useMutation } from 'react-query'

import { $fanId } from '@/models/chattingUI/chats/chats.ts'
import { addMessageByFanId } from '@/models/chattingUI/messages/messages.ts'
import { $creatorId } from '@/models/creators.ts'
import { toast } from '@/models/toasts'
import type { Message } from '@/modules/chatting/types'
import { handleError } from '@/utils/error'

import { rfGroupByTagType } from './consts'

interface Options {
  onRemoveLastTag: () => void
  message: Message
}

export const useManageTags = ({ message, onRemoveLastTag }: Options) => {
  const [tags, setTags] = useState(message.releaseForms)
  const { releaseForms, id } = message
  const creatorId = useUnit($creatorId)!
  const fanId = useUnit($fanId)!

  const removeTagMutation = useMutation(
    (tagId: number) => {
      const payload = (releaseForms ?? []).reduce<
        Required<EditReleaseFormRequest>
      >(
        (payload, tag) => {
          if (tag.id === tagId || !tag.type) return payload

          const tagType = rfGroupByTagType[tag.type]

          if (tagType) {
            payload[tagType].push(tag.id)
          }

          return payload
        },
        { messageId: id, rfGuest: [], rfTag: [], rfPartner: [] },
      )

      return OfReleaseFormsService.editReleaseForm({
        creatorId,
        requestBody: payload,
      })
    },
    {
      onSuccess({ releaseForms = [] }) {
        const newMessage: Message = {
          ...message,
          releaseForms,
        }

        addMessageByFanId({
          fanId: fanId,
          message: newMessage,
        })
        toast.success('Tag has been removed')
        if (releaseForms.length) {
          setTags(releaseForms)
        } else {
          onRemoveLastTag()
        }
      },
      onError(error) {
        handleError(error, 'Something went wrong while removing tag')
      },
    },
  )

  const handleEditTag = (id: number, name: string) => {
    const releaseForms = message.releaseForms?.map((releaseForm) =>
      releaseForm.id === id
        ? {
            ...releaseForm,
            name,
          }
        : releaseForm,
    )
    const newMessage = {
      ...message,
      releaseForms,
    }

    setTags(releaseForms)
    addMessageByFanId({ fanId, message: newMessage })
  }

  return {
    onRemove: removeTagMutation.mutate,
    isRemoving: removeTagMutation.isLoading,
    handleEditTag,
    tags,
  }
}
